import { Cascader, Checkbox } from 'antd'
import { CascaderOptionType } from 'antd/lib/cascader'
import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import shallow from 'zustand/shallow'
import PerFlexRow from '../../../components/custom_components/FlexRow'
import { PerInfoBox } from '../../../components/custom_components/InfoBox'
import { InfoBoxTile } from '../../../components/custom_components/InfoBox/InfoBoxTileTemplate/InfoBoxTile'
import { LoadingAndErrors } from '../../../components/custom_components/LoadingAndErrors'
import { PerInfoBoxDivider } from '../../../components/custom_components/InfoBox/InfoBoxDivider'
import { PerInfoBoxActionButtonElement } from '../../../components/custom_components/InfoBox/InfoBoxActionButtonElement'
import { useForecastModalStore } from '../../../components/custom_components/ForecastModal'
import { usePyramidForecastSelectorYearAndMonth } from '../../../graphql/pyramids/hooks/usePyramidForecastSelectorYearAndMonth'
import usePyramidSelectorYearAndMonth from '../../../graphql/pyramids/hooks/usePyramidSelectorYearAndMonth'
import { Country, PyramidType } from '../../../types'
import '../pyramids.less'
import { usePyramidsStore } from '../PyramidsStore'
import { mapCheckboxYearOptions } from '../pyramidUtils'

interface Props {
  infoType: PyramidType
  companyId: string
  country: Country
}

interface CompareCheckboxProps {
  label: string
  checked: boolean
  onCheckboxChange: (state: boolean) => void
  disabled: boolean
  className: string
}

interface CompareCheckboxAndCascaderProps extends CompareCheckboxProps {
  options?: CascaderOptionType[] | any
  onCascaderChange: (value: string[]) => void
  value: string[]
  defaultValue: string[]
  showCascader: boolean
}

export const PyramidsInfoBox: FC<Props> = ({ companyId, country, infoType }) => {
  const [t] = useTranslation()
  const toggleForecastModal = useForecastModalStore((state) => state.toggleModal)
  const [
    pastDate,
    setPastDate,
    showPast,
    setShowPast,
    showCurrent,
    setShowCurrent,
    showForecast,
    setShowForecast,
    forecastDate,
    setForecastDate,
  ] = usePyramidsStore(
    (state) => [
      state.pastDate,
      state.setPastDate,
      state.showPast,
      state.setShowPast,
      state.showCurrent,
      state.setShowCurrent,
      state.showForecast,
      state.setShowForecast,
      state.forecastDate,
      state.setForecastDate,
    ],
    shallow,
  )

  const { data, error, loading } = usePyramidSelectorYearAndMonth(companyId, country, infoType)
  const { data: forecastData, error: forecastError, loading: forecastLoading } = usePyramidForecastSelectorYearAndMonth(companyId, country)

  if (loading || !data || !data.pyramidPastValueSelector || !data.pyramidPastValueSelector.options || error) {
    return <LoadingAndErrors isLoading={loading} hasNoData={!data || !data.pyramidPastValueSelector} error={error} />
  }

  if (
    forecastLoading ||
    !forecastData ||
    !forecastData.pyramidForecastValueSelector ||
    !forecastData.pyramidForecastValueSelector.options ||
    forecastError
  ) {
    return <LoadingAndErrors isLoading={loading} hasNoData={!data || !data.pyramidPastValueSelector} error={error} />
  }

  const pastOptions = data.pyramidPastValueSelector.options.map(mapCheckboxYearOptions)
  const forecastOptions = forecastData.pyramidForecastValueSelector.options.map(mapCheckboxYearOptions)

  const handlePastPyramidChange = (value: string[]) => {
    if (value && value.length === 2) {
      setPastDate({ year: parseInt(value[0]), month: parseInt(value[1]) })
    }
  }

  const handleForecastPyramidChange = (value: string[]) => {
    if (value && value.length === 2) {
      setForecastDate({ year: parseInt(value[0]), month: parseInt(value[1]) })
    }
  }

  const InfoBoxCheckbox: FC<CompareCheckboxProps> = ({ label, checked, onCheckboxChange, disabled, className }) => {
    return (
      <Checkbox disabled={disabled} onChange={() => onCheckboxChange(!checked)} checked={checked}>
        <PerFlexRow noWrap align="middle" justify="center" m="xxs">
          <div className={`per-pyramids-settings-checkbox--${className}`}>{label}</div>
        </PerFlexRow>
      </Checkbox>
    )
  }

  const InfoBoxCheckboxAndCascader: FC<CompareCheckboxAndCascaderProps> = ({
    label,
    options,
    onCascaderChange,
    onCheckboxChange,
    disabled,
    value,
    defaultValue,
    checked,
    className,
    showCascader,
  }) => {
    return (
      <PerFlexRow noWrap align="middle">
        <InfoBoxCheckbox disabled={disabled} label={label} checked={checked} onCheckboxChange={onCheckboxChange} className={className} />
        <Cascader
          options={options}
          disabled={!showCascader}
          onChange={onCascaderChange}
          placeholder={t('pyramidsView.shared.infoBox.settings.cascaderPlaceholder')}
          size="small"
          value={value}
          defaultValue={defaultValue}
          style={{ width: '35%' }}
        />
      </PerFlexRow>
    )
  }

  return (
    <>
      <PerInfoBox>
        <InfoBoxTile
          heading={t('pyramidsView.shared.infoBox.settings.heading')}
          pyramidsMonthSelectionInfo={t('pyramidsView.shared.infoBox.settings.pyramidsMonthSelectionInfo')}
          contentFlex="growShrink"
        >
          <PerFlexRow direction="column" className="fullWidth">
            <InfoBoxCheckboxAndCascader
              label={t('pyramidsView.shared.legend.past')}
              disabled={pastOptions.length === 0}
              showCascader={showPast}
              options={pastOptions}
              onCascaderChange={handlePastPyramidChange}
              value={[pastDate.year.toString(), pastDate.month.toString()]}
              defaultValue={[pastDate.year.toString(), pastDate.month.toString()]}
              onCheckboxChange={setShowPast}
              checked={showPast}
              className="past"
            />
            <InfoBoxCheckbox
              disabled={false}
              label={t('pyramidsView.shared.legend.current')}
              onCheckboxChange={setShowCurrent}
              checked={showCurrent}
              className="current"
            />
            {forecastDate && (
              <InfoBoxCheckboxAndCascader
                label={t('pyramidsView.shared.legend.forecast')}
                disabled={forecastOptions.length === 0}
                showCascader={showForecast}
                options={forecastOptions}
                onCascaderChange={handleForecastPyramidChange}
                value={[forecastDate.year.toString(), forecastDate.month.toString()]}
                defaultValue={[forecastDate.year.toString(), forecastDate.month.toString()]}
                onCheckboxChange={setShowForecast}
                checked={showForecast && forecastOptions.length > 0}
                className="forecast"
              />
            )}
          </PerFlexRow>
        </InfoBoxTile>
        <PerInfoBoxDivider />
        <InfoBoxTile
          heading={t('pyramidsView.shared.infoBox.editForecast')}
          headingAlign="center"
          contentFlex="fixed"
          contentAlign="center"
        >
          <PerInfoBoxActionButtonElement icon="edit_fill" color="primary" onClick={() => toggleForecastModal(true)} />
        </InfoBoxTile>
      </PerInfoBox>
    </>
  )
}
